import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ProjectCard from '../components/ProjectCard';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const bedsOf = (p) => {
  const nums = String(p.beds || '').match(/\d+/g);
  return nums ? nums.map(Number) : [];
};
const sqftOf = (p) => {
  const n = String(p.sqft || '').replace(/[^0-9]/g, '');
  return n ? Number(n) : null;
};
const typesOf = (p) => (Array.isArray(p.types) && p.types.length ? p.types : (p.type ? [p.type] : []));
const PRICE_BANDS = {
  all: null, '0-2': [0, 2000000], '2-5': [2000000, 5000000], '5-10': [5000000, 10000000], '10+': [10000000, Infinity],
};

const ProjectsListing = () => {
  const { t, projects, developers } = useApp();
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  const statusParam = params.get('status') || 'all';
  const [dev, setDev] = useState('all');
  const [area, setArea] = useState('all');
  const [status, setStatus] = useState(statusParam);
  const [ptype, setPtype] = useState(params.get('type') || 'all');
  const [beds, setBeds] = useState('all');
  const [price, setPrice] = useState('all');
  const [furnishing, setFurnishing] = useState('all');
  const [sqftMin, setSqftMin] = useState('');
  const [sqftMax, setSqftMax] = useState('');
  const [query, setQuery] = useState(q);
  const areas = useMemo(() => Array.from(new Set(projects.map((p) => p.area).filter(Boolean))).sort(), [projects]);

  useEffect(() => { setQuery(params.get('q') || ''); setStatus(params.get('status') || 'all'); setPtype(params.get('type') || 'all'); }, [params]);
  useEffect(() => { document.title = 'New Projects in Dubai | GOBRA Capital Real Estate'; }, []);

  const filtered = useMemo(() => projects.filter((p) => {
    if (dev !== 'all' && p.developerId !== dev) return false;
    if (area !== 'all' && p.area !== area) return false;
    if (ptype !== 'all' && !typesOf(p).includes(ptype)) return false;
    if (beds !== 'all') {
      if (beds === 'studio') { if (!String(p.beds || '').toLowerCase().includes('studio')) return false; }
      else {
        const bn = bedsOf(p);
        if (beds === '4') { if (!bn.some((n) => n >= 4)) return false; }
        else if (!bn.includes(Number(beds))) return false;
      }
    }
    if (price !== 'all') {
      const [lo, hi] = PRICE_BANDS[price];
      const pr = p.priceAed || 0;
      if (!(pr >= lo && pr < hi)) return false;
    }
    if (furnishing !== 'all' && p.furnishing !== furnishing) return false;
    if (sqftMin || sqftMax) {
      const sq = sqftOf(p);
      if (sq === null) return false;
      if (sqftMin && sq < Number(sqftMin)) return false;
      if (sqftMax && sq > Number(sqftMax)) return false;
    }
    // Rentals surface only under the "For Rent" view; every other view hides them.
    if (status === 'rent') { if (p.status !== 'rent') return false; }
    else {
      if (p.status === 'rent') return false;
      if (status !== 'all' && p.status !== status) return false;
    }
    if (query) {
      const s = query.toLowerCase();
      if (!(p.name.toLowerCase().includes(s) || p.area.toLowerCase().includes(s) || p.developer.toLowerCase().includes(s))) return false;
    }
    return true;
  }), [dev, area, status, ptype, beds, price, furnishing, sqftMin, sqftMax, query, projects]);

  const isRent = status === 'rent';

  return (
    <main className="pt-24 md:pt-28 min-h-screen">
      <div className="bg-gobra-slate py-10 md:py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="h-1 w-12 bg-gobra-orange rounded-full mb-3" />
          <h1 className="font-title text-3xl md:text-5xl font-bold text-white">{isRent ? t('nav_rent') : t('nav_projects')}</h1>
          <p className="text-white/60 mt-2">{filtered.length} {(isRent ? t('nav_rent') : t('nav_projects')).toLowerCase()} in Dubai</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="bg-white rounded-xl border border-gobra-sand p-4 md:p-5 mb-8 flex flex-col md:flex-row md:flex-wrap gap-3 md:items-center">
          <div className="flex items-center gap-2 text-gobra-slate font-medium shrink-0">
            <SlidersHorizontal className="h-4 w-4 text-gobra-terracotta" /> {t('filter')}
          </div>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('search_placeholder')}
            className="flex-1 min-w-[180px] bg-gobra-sand rounded-lg px-4 py-2.5 outline-none text-sm" />
          <Select value={dev} onValueChange={setDev}>
            <SelectTrigger className="md:w-44"><SelectValue placeholder={t('all_developers')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_developers')}</SelectItem>
              {developers.filter((d) => d.id).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="md:w-44"><SelectValue placeholder={t('all_areas')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_areas')}</SelectItem>
              {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ptype} onValueChange={setPtype}>
            <SelectTrigger className="md:w-40" data-testid="filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Apartments">Apartments</SelectItem>
              <SelectItem value="Villas">Villas</SelectItem>
              <SelectItem value="Townhouses">Townhouses</SelectItem>
              <SelectItem value="Commercial">Commercial</SelectItem>
              <SelectItem value="Offices">Offices</SelectItem>
            </SelectContent>
          </Select>
          <Select value={beds} onValueChange={setBeds}>
            <SelectTrigger className="md:w-40" data-testid="filter-beds"><SelectValue placeholder="Bedrooms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Beds</SelectItem>
              <SelectItem value="studio">Studio</SelectItem>
              <SelectItem value="1">1 Bedroom</SelectItem>
              <SelectItem value="2">2 Bedrooms</SelectItem>
              <SelectItem value="3">3 Bedrooms</SelectItem>
              <SelectItem value="4">4+ Bedrooms</SelectItem>
            </SelectContent>
          </Select>
          <Select value={price} onValueChange={setPrice}>
            <SelectTrigger className="md:w-44" data-testid="filter-price"><SelectValue placeholder="Price" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Price</SelectItem>
              <SelectItem value="0-2">Up to AED 2M</SelectItem>
              <SelectItem value="2-5">AED 2M – 5M</SelectItem>
              <SelectItem value="5-10">AED 5M – 10M</SelectItem>
              <SelectItem value="10+">AED 10M+</SelectItem>
            </SelectContent>
          </Select>
          <Select value={furnishing} onValueChange={setFurnishing}>
            <SelectTrigger className="md:w-44" data-testid="filter-furnishing"><SelectValue placeholder="Furnishing" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Furnishing</SelectItem>
              <SelectItem value="Furnished">Furnished</SelectItem>
              <SelectItem value="Semi-Furnished">Semi-Furnished</SelectItem>
              <SelectItem value="Shell & Core">Shell &amp; Core</SelectItem>
            </SelectContent>
          </Select>
          {(ptype === 'Commercial' || ptype === 'Offices') && (
            <div className="flex items-center gap-2" data-testid="sqft-range">
              <input type="number" min="0" value={sqftMin} onChange={(e) => setSqftMin(e.target.value)} placeholder="Min SQFT" data-testid="filter-sqft-min"
                className="md:w-28 bg-gobra-sand rounded-lg px-3 py-2.5 outline-none text-sm" />
              <span className="text-muted-foreground text-sm">–</span>
              <input type="number" min="0" value={sqftMax} onChange={(e) => setSqftMax(e.target.value)} placeholder="Max SQFT" data-testid="filter-sqft-max"
                className="md:w-28 bg-gobra-sand rounded-lg px-3 py-2.5 outline-none text-sm" />
            </div>
          )}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="md:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('nav_projects')}</SelectItem>
              <SelectItem value="offplan">{t('nav_offplan')}</SelectItem>
              <SelectItem value="comingsoon">{t('coming_soon')}</SelectItem>
              <SelectItem value="rent">{t('nav_rent')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">No projects match your filters.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {filtered.map((p) => <ProjectCard key={p.id} project={p} />)}
          </div>
        )}
      </div>
    </main>
  );
};

export default ProjectsListing;
